import { useEffect } from "react";

/**
 * Blendet alle [data-reveal] im übergebenen Container beim Scrollen ein.
 *
 * `resetKey` neu beobachten lassen, wenn der Inhalt ausgetauscht wird (z. B.
 * Sprachwechsel): dabei ändern sich die React-Keys, die alten Elemente werden
 * unmounted und die neuen kommen wieder mit `data-reveal` und `opacity: 0` –
 * ohne erneutes Beobachten bliebe die Seite leer.
 *
 * Nach dem Einblenden werden Attribut und Klasse wieder entfernt. Die
 * Reveal-Regel setzt `transition: opacity, transform` und würde sonst die
 * eigene Transition der Glaskarten überschreiben – der Hover-Effekt käme
 * dann verzögert und ohne Übergang für Schatten und Hintergrund.
 */
export const useReveal = (ref, resetKey) => {
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;

    const targets = root.querySelectorAll("[data-reveal]");
    const strip = (el) => {
      el.classList.remove("is-visible");
      el.removeAttribute("data-reveal");
    };

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduced || typeof IntersectionObserver === "undefined") {
      targets.forEach(strip);
      return undefined;
    }

    const timers = new Set();
    // Listener aufheben, damit das Cleanup sie auch dann entfernt, wenn beim
    // Unmount noch ein Reveal läuft.
    const pending = new Map();

    const settle = (el) => {
      const done = (event) => {
        // `transitionend` blubbert. Ohne die Zielprüfung beendet schon die
        // Opacity-Transition eines Kindes – etwa ein Chip oder ein Icon – das
        // Reveal der Karte, bevor diese selbst fertig eingeblendet ist.
        if (event && (event.target !== el || event.propertyName !== "opacity"))
          return;
        el.removeEventListener("transitionend", done);
        pending.delete(el);
        clearTimeout(timer);
        timers.delete(timer);
        strip(el);
      };
      // Fallback, falls transitionend ausbleibt (Tab im Hintergrund o. Ä.)
      const timer = setTimeout(done, 2000);
      timers.add(timer);
      pending.set(el, done);
      el.addEventListener("transitionend", done);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          entry.target.classList.add("is-visible");
          settle(entry.target);
        });
      },
      // Positives unteres Margin: der Beobachtungsbereich reicht über den
      // unteren Viewportrand hinaus, das Einblenden startet also *bevor* die
      // Karte ins Bild kommt. Vorher stand hier -10%, was den Bereich
      // verkleinerte: eine Karte musste erst 10% Bildschirmhöhe über den
      // unteren Rand wandern, stand damit längst vollständig im Bild und war
      // trotzdem noch unsichtbar. Gemessen waren das ~200-300 ms leere Fläche
      // nach einem schnellen Scrollsprung.
      { rootMargin: "0px 0px 15% 0px", threshold: 0 },
    );

    targets.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
      timers.forEach(clearTimeout);
      pending.forEach((done, el) =>
        el.removeEventListener("transitionend", done),
      );
    };
  }, [ref, resetKey]);
};

export default useReveal;
